const STATIC_CLIENT_ENV = {
  REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || "",
  REACT_APP_APP_ENV: process.env.REACT_APP_APP_ENV || "",
  VITE_ENVIRONMENT: process.env.VITE_ENVIRONMENT || "",
  VITE_APP_ENV: process.env.VITE_APP_ENV || "",
  NODE_ENV: process.env.NODE_ENV || "",
};

const PRODUCTION_PORTAL_HOSTS = new Set(["ecotourodisha.com", "www.ecotourodisha.com"]);
const PRODUCTION_ENVIRONMENT_NAMES = new Set(["prod", "production"]);
export const PORTAL_BRAND_TITLE = "EcoTour Odisha";
export const PORTAL_OPERATOR_TITLE = "Wildlife Department, Government of Odisha";
const LEGACY_OPERATOR_ACRONYM = ["O", "F", "D", "C"].join("");
const LEGACY_OPERATOR_NAME_PATTERN = ["Odisha", "Forest", "Development", "Corporation"].join("\\s+");

const sanitizeEnvValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized.indexOf("%react_app_") === 0 || normalized.indexOf("%vite_") === 0) return "";
  if (normalized.indexOf("%") !== -1) return "";
  return normalized;
};

const readRuntimeEnv = (key) => {
  if (
    typeof window !== "undefined" &&
    window.__PORTAL_BUILD_ENV__ &&
    window.__PORTAL_BUILD_ENV__[key] !== undefined
  ) {
    const runtimeValue = sanitizeEnvValue(window.__PORTAL_BUILD_ENV__[key]);
    if (runtimeValue) return runtimeValue;
  }
  if (typeof window !== "undefined" && window.__VITE_ENV__ && window.__VITE_ENV__[key] !== undefined) {
    const runtimeValue = sanitizeEnvValue(window.__VITE_ENV__[key]);
    if (runtimeValue) return runtimeValue;
  }
  if (
    typeof window !== "undefined" &&
    window.__PORTAL_ENV_DEBUG__ &&
    key === "REACT_APP_ENVIRONMENT"
  ) {
    const runtimeValue = sanitizeEnvValue(window.__PORTAL_ENV_DEBUG__.resolvedEnv);
    if (runtimeValue) return runtimeValue;
  }
  return "";
};

const readClientEnv = (key) => {
  const runtimeValue = readRuntimeEnv(key);
  if (runtimeValue) return runtimeValue;
  if (Object.prototype.hasOwnProperty.call(STATIC_CLIENT_ENV, key)) {
    return sanitizeEnvValue(STATIC_CLIENT_ENV[key]);
  }
  return "";
};

const normalizeEnvironmentValue = (...values) => {
  for (const candidate of values) {
    const normalized = sanitizeEnvValue(candidate);
    if (normalized) return normalized;
  }
  return "";
};

const isProductionEnvironmentName = (value) => PRODUCTION_ENVIRONMENT_NAMES.has(sanitizeEnvValue(value));

export const isPortalTestEnvironmentName = (value) => {
  const normalized = sanitizeEnvValue(value);
  return Boolean(normalized) && !isProductionEnvironmentName(normalized);
};

const isProductionPortalHost = (options = {}) => {
  const hostname =
    options.hostname !== undefined
      ? String(options.hostname || "").trim().toLowerCase()
      : typeof window !== "undefined"
      ? String(window.location?.hostname || "").trim().toLowerCase()
      : "";
  return PRODUCTION_PORTAL_HOSTS.has(hostname);
};

let portalEnvDebugLogged = false;

export const TEST_PORTAL_TITLE = "EcoTour Odisha Test Site";
export const TEST_PORTAL_DESCRIPTION =
  "EcoTour Odisha test site for user testing only. This is not the public production site.";
export const TEST_PORTAL_ROBOTS =
  "noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate, max-snippet:0, max-image-preview:none, max-video-preview:0";

export const resolvePortalEnvironmentName = (options = {}) => {
  const explicitEnvironment = normalizeEnvironmentValue(
    readClientEnv("REACT_APP_ENVIRONMENT"),
    readClientEnv("REACT_APP_APP_ENV"),
    readClientEnv("VITE_ENVIRONMENT"),
    readClientEnv("VITE_APP_ENV"),
  );
  if (isProductionPortalHost(options) && isPortalTestEnvironmentName(explicitEnvironment)) {
    return "production";
  }
  return explicitEnvironment || normalizeEnvironmentValue(readClientEnv("NODE_ENV"));
};

export const getPortalEnvironmentName = () => resolvePortalEnvironmentName();

export const isPortalTestEnvironment = () => isPortalTestEnvironmentName(getPortalEnvironmentName());

export const logPortalEnvironmentDebug = () => {
  if (portalEnvDebugLogged) return;
  portalEnvDebugLogged = true;

  if (typeof window === "undefined" || typeof console?.info !== "function") {
    return;
  }

  console.info("[PortalTestDebug][runtime]", {
    source: "environmentMode",
    reactAppEnvironment: readClientEnv("REACT_APP_ENVIRONMENT"),
    reactAppAppEnv: readClientEnv("REACT_APP_APP_ENV"),
    viteEnvironment: readClientEnv("VITE_ENVIRONMENT"),
    viteAppEnv: readClientEnv("VITE_APP_ENV"),
    nodeEnv: readClientEnv("NODE_ENV"),
    resolvedEnv: getPortalEnvironmentName(),
    isTest: isPortalTestEnvironment(),
    htmlDebug: window.__PORTAL_ENV_DEBUG__ || null,
    host: window.location.host,
    path: window.location.pathname,
  });
};

export const sanitizePortalBrandText = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized
    .replace(
      new RegExp(`\\b${LEGACY_OPERATOR_ACRONYM}\\s+ECO[-\\s]*TOURISM\\b`, "gi"),
      PORTAL_BRAND_TITLE,
    )
    .replace(/\bGOVERNMENT\s+OF\s+ODISHA\s+ECO[-\s]*TOURISM\b/gi, PORTAL_BRAND_TITLE)
    .replace(
      new RegExp(`\\b${LEGACY_OPERATOR_NAME_PATTERN}(?:\\s+Limited|\\s+Ltd\\.?)?\\b`, "gi"),
      PORTAL_OPERATOR_TITLE,
    )
    .replace(
      new RegExp(`\\b${LEGACY_OPERATOR_ACRONYM}(?:'s|\\s+LTD|\\s+Ltd\\.?|\\s+Limited)?\\b`, "gi"),
      PORTAL_BRAND_TITLE,
    )
    .replace(/\s+/g, " ")
    .trim();
};

export const normalizePortalPageTitle = (pageTitle = "") => {
  const rawTitle = String(pageTitle || "").trim();
  const legacyOnlyTitlePattern = new RegExp(
    `^(?:${LEGACY_OPERATOR_ACRONYM}\\s+ECO[-\\s]*TOURISM|GOVERNMENT\\s+OF\\s+ODISHA\\s+ECO[-\\s]*TOURISM|ODISHA\\s+ECO[-\\s]*TOURISM)$`,
    "i",
  );
  if (legacyOnlyTitlePattern.test(rawTitle)) {
    return "";
  }
  const normalized = sanitizePortalBrandText(rawTitle)
    .replace(/\s*(?:\||-|--|•)\s*(?:Eco\s*Tour\s*Odisha|EcoTour\s*Odisha|Odisha\s+Eco[-\s]*Tourism)\s*$/i, "")
    .replace(/\s*(?:\||-|--|•)\s*$/i, "")
    .trim();
  if (/^(?:Eco\s*Tour\s*Odisha|EcoTour\s*Odisha)$/i.test(normalized)) {
    return "";
  }
  return normalized;
};

export const buildPortalSeoTitle = (pageTitle = "") => {
  const normalized = normalizePortalPageTitle(pageTitle);
  if (!isPortalTestEnvironment()) return normalized ? `${normalized} | ${PORTAL_BRAND_TITLE}` : PORTAL_BRAND_TITLE;
  return normalized ? `${normalized} | ${TEST_PORTAL_TITLE}` : `${TEST_PORTAL_TITLE} | User Testing Only`;
};

export const buildPortalSeoDescription = (description = "") => {
  if (isPortalTestEnvironment()) return TEST_PORTAL_DESCRIPTION;
  return sanitizePortalBrandText(description);
};
